import React from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';

import { formatDate, formatNull, formatOverflow, isDatePastInMoscow } from '../../../Utils/GlobalUtils';

import { useTimeModal } from '../../../Components/TimeModal/TimeModalContext';

import AddNewDocumentToApplication from '../../AddNewDocumentToApplication';

import SearchAMProposedCases from '../../SearchModals/SearchAMProposedCases';
import FilterAMProposedCases from '../../FilterComponents/FilterAMProposedCases';

import Loading from '../../../Components/Loading';
import LoadingLines from '../../../Components/LoadingLines';
import WarningPanel from '../../../Components/WarningPanel';

import ButtonWithConfirmation from '../../ButtonWithConfirmation';

import DismissibleBanner from '../../DismissibleBanner';

import headerPanel from '../../../styles/HeaderPanel.module.css';

import IconContainer from '../../../Components/IconContainer';
import { ReactComponent as ReloadIcon } from '../../../icons/reload.svg';
import { ReactComponent as TrashIcon } from '../../../icons/trash.svg';
import { ReactComponent as FunnelIcon } from '../../../icons/funnel-bold.svg';
import { ReactComponent as FunnelFillIcon } from '../../../icons/funnel-fill.svg';

const MainTableOfAMProposedCases = ({ userId }) => {
    const { openTimeModal } = useTimeModal();
    const [searchParams, setSearchParams] = useSearchParams();
    
    const [cases, setCases] = React.useState([]);
    const [warning, setWarning] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const [reload, setReload] = React.useState(true);

    const [limitForView, setLimitForView] = React.useState(50);
    const [lastUpdateTime, setLastUpdateTime] = React.useState('');

    const DEFAULT_FILTERS = {
        numbers: [],
    };

    const STORAGE_KEY = 'amapproposedcases_filters';

    const [filters, setFilters] = React.useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            return saved ? { ...DEFAULT_FILTERS, ...JSON.parse(saved) } : DEFAULT_FILTERS;
        } catch {
            return DEFAULT_FILTERS;
        }
    });

    React.useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
    }, [filters]);

    const hasActiveFilters = Object.entries(filters).some(([key, value]) => 
        Array.isArray(value) ? value.length > 0 : Boolean(value)
    );

    React.useEffect(() => {
        const token = localStorage.getItem('token');

        setLoading(true);

        if (!hasActiveFilters) {
            axios.get(`/api/get-user-proposed-cases`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    user_id: userId,
                    limitForView
                }
            }).then((response) => {
                setWarning('');
                setCases(response.data);
            })
            .catch((error) => {
                console.error(error);
                setWarning(error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее');
            })
            .finally(() => {
                setReload(false);
                setLoading(false);
                setLastUpdateTime(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
            });
        } else {
            axios.post(`/api/get-user-proposed-cases-filtered`, filters, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    user_id: userId,
                    limitForView
                }
            }).then((response) => {
                setWarning('');
                setCases(response.data);
            })
            .catch((error) => {
                console.error(error);
                setWarning(error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее');
            })
            .finally(() => {
                setReload(false);
                setLoading(false);
                setLastUpdateTime(new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }));
            });
        }
    }, [reload, userId, hasActiveFilters]);

    const changeIsVisible = (application_id) => {
        axios.put(`/api/change-is-visible-application?application_id=${application_id}`)
        .then(() => {
            openTimeModal('Заявка перемещена в корзину');
        })
        .catch((error) => {
            console.error(error);
            setWarning(error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее');
        })
        .finally(() => {
            setReload(true);
        });
    };

    const [isNewDocumentToApplication, setIsNewDocumentToApplication] = React.useState(false);
    const [selectedApplicationId, setSelectedApplicationId] = React.useState('');
    const [selectedCaseId, setSelectedCaseId] = React.useState('');

    const [isFilter, setIsFilter] = React.useState(false);
    const [fieldFilter, setFieldFilter] = React.useState(null);

    React.useEffect(() => {
        if (window.location.hash === '#filter') {
            setIsFilter(true);
        }
    }, []);

    const [isSearch, setIsSearch] = React.useState(false);


    return (
    <>
        {isFilter && <FilterAMProposedCases field={fieldFilter} filters={filters} setFilters={setFilters} DEFAULT_FILTERS={DEFAULT_FILTERS} userId={userId} closeModal={() => setIsFilter(false)} />}
        {isSearch && <SearchAMProposedCases closeModal={() => {setIsSearch(false); setReload(true)}} userId={userId} />}
        {isNewDocumentToApplication && 
        <AddNewDocumentToApplication 
            applicationId={selectedApplicationId} 
            caseId={selectedCaseId} 
            isFirst={true} 
            closeModal={() => {setIsNewDocumentToApplication(false)}} 
            onSuccess={() => setCases(prevCases =>
                prevCases.map(item =>
                    item.id === selectedApplicationId ? { ...item, isSent: true } : item
                )
            )}
        />}

        {/* <DismissibleBanner 
            id="new-feauture-filters-for-am-proposed-cases-page" 
            headline='Новая функция'
            text={
                <span
                    style={{display: 'flex', flexDirection: 'column', alignItems: 'start', gap: '3px'}}
                >
                    <h3 style={{fontSize: '15px', fontWeight: '500'}}>Фильтр по витрине заявок</h3>
                    Теперь у Вас есть возможность задавать фильтр по номеру заявки для более точного поиска среди всего списка. Для установки фильтра наведите указатель мыши на шапку таблицы и нажмите кнопку фильтра.
                </span>
            }
        /> */}

        <div className={headerPanel.panel}>
            <div className={headerPanel.searchBtn} onClick={() => setIsSearch(true)}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path fill="currentColor" d="M21.71,20.29,18,16.61A9,9,0,1,0,16.61,18l3.68,3.68a1,1,0,0,0,1.42,0A1,1,0,0,0,21.71,20.29ZM11,18a7,7,0,1,1,7-7A7,7,0,0,1,11,18Z" /></svg>
                Поиск...
            </div>
            <button
                type='button'
                className='button miniButton'
                style={{width: 'fit-content'}}
                onClick={() => setSearchParams({ section: 'trash' })}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#000000" viewBox="0 0 256 256"><path d="M216,48H180V36A28,28,0,0,0,152,8H104A28,28,0,0,0,76,36V48H40a12,12,0,0,0,0,24h4V208a20,20,0,0,0,20,20H192a20,20,0,0,0,20-20V72h4a12,12,0,0,0,0-24ZM100,36a4,4,0,0,1,4-4h48a4,4,0,0,1,4,4V48H100Zm88,168H68V72H188ZM116,104v64a12,12,0,0,1-24,0V104a12,12,0,0,1,24,0Zm48,0v64a12,12,0,0,1-24,0V104a12,12,0,0,1,24,0Z"></path></svg>
                Корзина
            </button>
        </div>

        <div className='horizontalContainer'>
            <div className='boxPage'>
                <div className='boxHeader'>
                    <h3>Информация</h3>
                </div>
                <div className='boxMain' style={{fontSize: '14px'}}>
                    <div>Данная таблица отображает предложения по заявкам, направленные вам СРО. Чтобы посмотреть заявки, на которые вы уже отправили согласие, перейдите в <Link className='aWithLink' to="/applications">"Мои заявки"</Link></div>
                </div>
            </div>
        </div>
      
        <div className='pageWrapper'>
            <div className='tableContainer'>
                <div className='tableHeaderWrapper'>
                    <div className='tableStatus'>
                        <div>
                            {hasActiveFilters && 
                                <span className='horizontalContainer'>
                                    <p>Применены фильтры</p>
                                    <p 
                                        className='aWithLink'
                                        onClick={() => {setFilters(DEFAULT_FILTERS); setReload(true)}}
                                    >
                                        Сбросить
                                    </p>
                                </span>
                            }
                        </div>
                        <a className='lastUpdateWrapper'>
                            Последнее обновление таблицы: {lastUpdateTime || '--:--'} 
                            {loading ? 
                                <Loading type='inline' /> 
                            : 
                                <IconContainer Icon={ReloadIcon} onClick={() => setReload(true)} size = '14' color = 'black' className = '' />
                            }
                        </a>
                    </div>
                    <div className='tableHeader' style={{gridTemplateColumns: '19fr 25fr 12fr 13fr 12fr 13fr 4fr'}}>
                        <div>
                            <p>Номер</p>
                            <IconContainer 
                                Icon={filters.numbers.length > 0 ? FunnelFillIcon : FunnelIcon} 
                                onClick={() => {
                                    setIsFilter(true);
                                    setFieldFilter('numbers');
                                }} 
                                size = '15' 
                                color = 'black' 
                                className = {`
                                    ${'filterHeaderIcon'} 
                                    ${filters.numbers.length > 0 && 'showedFilterHeaderIcon'}
                                `}
                            />
                        </div>
                        <div>Процедура / Должник</div>
                        <div>Дата рассылки предложения</div>
                        <div>Крайний срок предоставления согласия</div>
                        <div>Количество претендующих</div>
                        <div></div>
                        <div></div>
                    </div>
                </div>
                {(cases.length===0 && loading) ? <LoadingLines /> 
                : warning ? <WarningPanel text={warning} inTable={true} buttonText='Обновить' buttonAction={() => setReload(true)} />
                : cases.map((caseItem) =>
                    caseItem.isSent ? (
                        <div
                            className='tableRow nonActive successRow'
                            key={caseItem.id}
                            style={{ gridTemplateColumns: '1fr' }}
                        >
                            <div
                                style={{height: '40px', margin: '0 15px'}}
                            >
                                <p
                                    style={{display: 'flex', alignItems: 'center', gap: '15px'}}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-check-circle-fill" viewBox="0 0 16 16">
                                        <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                    </svg>
                                    Согласие отправлено. Отслеживать статус заявки можно во вкладке «Мои заявки»
                                </p>
                            </div>
                        </div>
                    ) : (
                    <div 
                        className='tableRow nonActive' 
                        key={caseItem.id} 
                        style={{gridTemplateColumns: '19fr 25fr 12fr 13fr 12fr 13fr 4fr'}}
                    >
                        <div>
                            <p>{formatNull(caseItem.number)}</p>
                            {caseItem.case_link && <a className='aWithLink' href={caseItem.case_link}>Ссылка</a>}
                        </div>
                        <div>
                            <span className='CellForHorizontalLine'>
                                <p
                                    className='grayText'
                                >
                                    {formatNull(caseItem.procedure_name)}
                                </p>
                                <span className='horizontalLine'></span>
                                {caseItem.debtors ?
                                    <p>
                                        {formatOverflow(caseItem.debtors.map((e) => e.name).join(', '))}
                                    </p>
                                    :
                                    formatNull(caseItem.debtors)
                                }
                            </span>
                        </div>
                        <div>{caseItem.mailing_status==='Письма не отправлены' ? <p>Письма не отправлены</p> : formatDate(caseItem.date_mailing)}</div>
                        <div>
                            <p>{formatDate(caseItem.date_consent_deadline)}</p>
                            <p>{!caseItem.is_consent_deadline && '[СРО временно продлила срок приёма согласий]'}</p>
                        </div>
                        <div>
                            {caseItem.status=='Управляющий не назначен' ? 
                            <p className='description'>{caseItem.status}</p>
                            :
                            caseItem.user ? 
                            <p className='description'>Кандидатура управляющего выбрана СРО</p>
                            :
                            <p>{caseItem.count_pretenders}</p>}
                        </div>
                        <div>
                            {(caseItem.user_id!=='' && caseItem.user_id!==null) &&
                            <button 
                                type='button' 
                                className='miniButton' 
                                onClick={() => {
                                    if (!isDatePastInMoscow(caseItem.date_consent_deadline) || !caseItem.is_consent_deadline) {
                                        setIsNewDocumentToApplication(true); setSelectedApplicationId(caseItem.id); setSelectedCaseId(caseItem.case_id);
                                    } else {
                                        openTimeModal('Крайний срок предоставления согласия прошел', 'warning');
                                    }
                                }}
                                style={(!isDatePastInMoscow(caseItem.date_consent_deadline) || !caseItem.is_consent_deadline) ? {} : {opacity: '.3'}}
                            >
                                Отправить согласие
                            </button>}
                        </div>
                        <div
                            style={{alignItems: 'end'}}
                        >
                            <ButtonWithConfirmation
                                entryFunc={() => changeIsVisible(caseItem.id)}
                                Icon={TrashIcon} 
                                IconSize={17}
                                buttonName='Переместить в корзину' 
                                color='red' 
                            />
                        </div>
                    </div>
                ))}
                {!warning && cases.length > 0 && (cases.length < cases[0].total_count) && (
                    <div className='tableLoadMoreRow' style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                        <div></div>
                        <div>
                            <button
                                type='button'
                                className='button'
                                style={{ width: 'fit-content' }}
                                onClick={() => {setLimitForView(prev => prev + 50); setReload(true)}}
                                disabled={loading}
                            >
                                {loading ? <Loading type='inline' /> : 'Загрузить ещё'}
                            </button>
                        </div>
                        <div></div>
                    </div>
                )}
            </div>
        </div>
    </>
    )
}

export default MainTableOfAMProposedCases;